import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Button,
    message,
    Space,
    Typography,
    Card,
    Divider,
    Alert,
} from 'antd';
import {
    QrcodeOutlined,
    KeyOutlined,
    SafetyOutlined,
    CopyOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { generateTwoFactor, enableTwoFactor } from '@/services/ant-design-pro/api';

const { Text, Paragraph } = Typography;

interface Google2FAModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

const Google2FAModal: React.FC<Google2FAModalProps> = ({
    visible,
    onCancel,
    onSuccess,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [twoFactorData, setTwoFactorData] = useState<{
        secret: string;
        qrCodeUrl: string;
        username: string;
    } | null>(null);

    // 获取二维码和密钥
    const fetchTwoFactorData = async () => {
        setGenerating(true);
        try {
            const response = await generateTwoFactor();
            if (response.code === 0 && response.data) {
                setTwoFactorData(response.data);
                form.setFieldsValue({ secret: response.data.secret });
            } else {
                message.error(response.msg || '获取验证码信息失败');
            }
        } catch (error) {
            message.error('获取验证码信息失败');
        } finally {
            setGenerating(false);
        }
    };

    // 启用谷歌验证码
    const handleEnable = async (values: { token: string }) => {
        if (!twoFactorData) {
            message.error('请先获取验证码信息');
            return;
        }

        setLoading(true);
        try {
            const response = await enableTwoFactor({
                secret: twoFactorData.secret,
                token: values.token,
            });

            if (response.code === 0) {
                message.success('谷歌验证码绑定成功！即将退出登录...');
                // 延迟一下让用户看到成功消息
                setTimeout(() => {
                    onSuccess();
                }, 1500);
            } else {
                message.error(response.msg || '绑定失败，请检查验证码是否正确');
            }
        } catch (error) {
            message.error('绑定失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    };

    // 复制密钥到剪贴板
    const copySecret = () => {
        if (twoFactorData?.secret) {
            navigator.clipboard.writeText(twoFactorData.secret);
            message.success('密钥已复制到剪贴板');
        }
    };

    // 重置表单
    const handleCancel = () => {
        form.resetFields();
        setTwoFactorData(null);
        onCancel();
    };

    // 当弹窗打开时自动获取数据
    useEffect(() => {
        if (visible && !twoFactorData) {
            fetchTwoFactorData();
        }
    }, [visible]);

    return (
        <Modal
            title={
                <Space>
                    <SafetyOutlined />
                    绑定谷歌验证码
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={500}
            destroyOnClose
        >
            <Alert
                message="安全提示"
                description="请使用 Google Authenticator 或类似的验证器应用扫描二维码，然后输入生成的6位验证码完成绑定。"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleEnable}
            >
                {/* 二维码区域 */}
                <Card
                    size="small"
                    style={{ marginBottom: 16 }}
                    extra={
                        <Button
                            type="text"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={fetchTwoFactorData}
                            loading={generating}
                            disabled={generating}
                            title="刷新二维码"
                        />
                    }
                >
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ marginBottom: 16 }}>
                            <QrcodeOutlined style={{ fontSize: 24, color: '#1890ff', marginRight: 8 }} />
                            <Text strong style={{ fontSize: 16 }}>扫描二维码</Text>
                        </div>
                        {twoFactorData?.qrCodeUrl ? (
                            <img
                                src={twoFactorData.qrCodeUrl}
                                alt="Google Authenticator QR Code"
                                style={{
                                    width: '100%',
                                    maxWidth: 250,
                                    height: 'auto',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 8,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                }}
                            />
                        ) : (
                            <div style={{
                                width: 250,
                                height: 250,
                                border: '2px dashed #d9d9d9',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#999',
                                backgroundColor: '#fafafa',
                            }}>
                                {generating ? '生成中...' : '暂无二维码'}
                            </div>
                        )}
                    </div>
                </Card>

                {/* 密钥区域 */}
                <Card size="small" style={{ marginBottom: 16 }}>
                    <div>
                        <div style={{ marginBottom: 12, textAlign: 'center' }}>
                            <KeyOutlined style={{ fontSize: 20, color: '#52c41a', marginRight: 8 }} />
                            <Text strong>手动输入密钥</Text>
                        </div>
                        {twoFactorData?.secret ? (
                            <div>
                                <div style={{ marginBottom: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        如果无法扫描二维码，请手动输入此密钥到验证器应用中：
                                    </Text>
                                </div>
                                <div style={{
                                    backgroundColor: '#f6ffed',
                                    border: '1px solid #b7eb8f',
                                    padding: 16,
                                    borderRadius: 8,
                                    position: 'relative',
                                }}>
                                    <Paragraph
                                        copyable={{
                                            text: twoFactorData.secret,
                                            icon: [<CopyOutlined key="copy" />, <CopyOutlined key="copied" />],
                                        }}
                                        style={{
                                            marginBottom: 0,
                                            fontFamily: 'monospace',
                                            fontSize: 16,
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            letterSpacing: '2px',
                                            color: '#52c41a',
                                        }}
                                    >
                                        {twoFactorData.secret}
                                    </Paragraph>
                                </div>
                            </div>
                        ) : (
                            <div style={{
                                backgroundColor: '#f5f5f5',
                                padding: 20,
                                borderRadius: 6,
                                textAlign: 'center',
                                color: '#999',
                                border: '1px dashed #d9d9d9',
                            }}>
                                {generating ? '生成中...' : '暂无密钥'}
                            </div>
                        )}
                    </div>
                </Card>


                {/* 验证码输入 */}
                <Form.Item
                    name="token"
                    label={
                        <span>
                            <SafetyOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                            验证码
                        </span>
                    }
                    rules={[
                        { required: true, message: '请输入6位验证码' },
                        { pattern: /^\d{6}$/, message: '请输入6位数字验证码' },
                    ]}
                >
                    <Input
                        placeholder="请输入Google Authenticator生成的6位验证码"
                        maxLength={6}
                        size="large"
                    />
                </Form.Item>

                {/* 操作按钮 */}
                <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
                    <Space>
                        <Button onClick={handleCancel}>
                            取消
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            disabled={!twoFactorData}
                        >
                            绑定验证码
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default Google2FAModal; 